import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Grid from "@mui/material/Grid2";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "sql-formatter";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { selectUserData } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { useLocation, useNavigate } from "react-router-dom";
import { formatUtcToHumanReadable } from "../DataStoreHelpers/dataStoreHelpers";

const QueryName = () => {
  const textFields = [
    { label: "type" },
    { label: "format" },
    { label: "order" },
    { label: "value" },
  ];
  const [expandedInput, setExpandedInput] = useState<string | false>(false);
  const [expandedOutput, setExpandedOutput] = useState<string | false>(false);

  const { queryContentSingle } = useSelector(
    (state: any) => state.dataStoreReq
  );

  const {
    description,
    database,
    query,
    input,
    output,
    name,
    id,
    type,
    modules,
    confluence_url,
    modified_by,
    modified,
  } = queryContentSingle !== null && queryContentSingle;

  const response = useSelector(selectUserData);
  const hasDataStoreEditPermission = CheckPermissions(
    response?.data?.products?.admin,
    "datastore_requests",
    "edit_datastore_requests"
  );
  const navigate = useNavigate();
  const location = useLocation();
  const [queryView, setQueryView] = useState(null) as any;

  const openEdit = (id: string) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("id", id); // Add or update `id` param

    navigate({
      pathname: "/admin/datastore_requests/edit_query",
      search: searchParams.toString(),
    });
  };

  useEffect(() => {
    const handleFormat = () => {
      try {
        const placeholderMap = {} as any;
        let tokenCounter = 0;

        const cleanedQuery = query.replace(/{{.*?}}/g, (match: any) => {
          const token = `__PLACEHOLDER_${tokenCounter}__`;
          placeholderMap[token] = match;
          tokenCounter++;
          return token;
        });

        const formattedQuery = format(cleanedQuery, { language: "bigquery" });

        const restoredQuery = formattedQuery.replace(
          /__PLACEHOLDER_\d+__/g,
          (match) => {
            return placeholderMap[match];
          }
        );

        setQueryView(restoredQuery);
      } catch (error) {
        console.error("Error formatting SQL:", error);
      }
    };

    handleFormat(); // Format on mount
  }, [query]);

  const handleChangeInput =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedInput(isExpanded ? panel : false);
    };

  const handleChangeOutput =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedOutput(isExpanded ? panel : false);
    };

  const modifiedProperties = Object.fromEntries(
    Object.entries(input?.properties ?? {}).map(([key, value]: any, index) => [
      key,
      { ...value, input: "Input " + (index + 1) }, // Create a new object instead of modifying the original one
    ])
  );

  const modifiedOutputProperties = Object.fromEntries(
    Object.entries(output?.properties ?? {}).map(([key, value]: any, index) => [
      key,
      { ...value, input: "Output " + (index + 1) }, // Create a new object instead of modifying the original one
    ])
  );

  const modifiedInput = { ...input, properties: modifiedProperties };
  const modifiedOutput = { ...output, properties: modifiedOutputProperties };
  const capitalizeFirstLetter = (string: string) => {
    return string
      .split("_") // Split by underscores
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
      .join(" "); // Join with spaces
  };

  return (
    <>
      <Paper sx={{ padding: "16px" }}>
        <Stack
          flexDirection={"row"}
          justifyContent={"space-between"}
          mb={2}
          alignItems={"center"}
        >
          <Typography fontSize={24} fontWeight={400}>
            {`${name} (${id})`}
          </Typography>
          {hasDataStoreEditPermission ? (
            <Button
              variant="contained"
              color="secondary"
              endIcon={<EditOutlinedIcon />}
              onClick={() => openEdit(id)}
              data-tracker="245"
            >
              Edit
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              endIcon={<EditOutlinedIcon />}
              disabled
            >
              Edit
            </Button>
          )}
        </Stack>
        <Stack gap={2}>
          <Typography>Info</Typography>
          <TextField
            multiline
            id="outlined-required"
            label="Query Description"
            color="secondary"
            value={description !== null ? description : ""}
            fullWidth
            InputProps={{
              readOnly: true,
            }}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            rows="auto"
          />
          <Box display="flex" gap={2}>
            <TextField
              variant="standard"
              label="Database"
              color="secondary"
              value={database ?? ""}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              InputProps={{ readOnly: true }}
            />
            <TextField
              variant="standard"
              label="Request Type"
              color="secondary"
              value={type ?? ""}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              InputProps={{ readOnly: true }}
            />
            <TextField
              variant="standard"
              label="Modules"
              color="secondary"
              value={modules ? modules.join(", ") : ""}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              InputProps={{ readOnly: true }}
            />
          </Box>
          <Box display="flex" gap={2}>
            <TextField
              variant="standard"
              label="Confluence URL"
              color="secondary"
              value={confluence_url ?? ""}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              InputProps={{ readOnly: true }}
            />
            <TextField
              variant="standard"
              label="Modified By"
              color="secondary"
              value={modified_by ?? ""}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              InputProps={{ readOnly: true }}
            />
            <TextField
              variant="standard"
              label="Modified"
              color="secondary"
              value={(modified && formatUtcToHumanReadable(modified)) ?? ""}
              fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              InputProps={{ readOnly: true }}
            />
          </Box>
          {/* SQL Code Display */}
          <div
            style={{
              position: "relative",
              display: "inline-block",
              width: "100%",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: "0px",
                left: "8px",
                background: "#f5f5f5",
                padding: "2px 6px",
                fontSize: "12px",
                borderRadius: "4px",
                zIndex: 1,
              }}
            >
              Request SQL
            </div>
            <SyntaxHighlighter
              language="sql"
              style={materialLight}
              customStyle={{
                padding: "20px 8px 8px", // Top padding increased to avoid label overlap
                borderRadius: "4px",
                fontSize: "14px",
                overflowX: "auto",
                maxHeight: "300px",
                height: "300px",
                border: "1px solid #ccc",
              }}
            >
              {queryView}
            </SyntaxHighlighter>
          </div>
          <Grid container spacing={2}>
            <Grid size={6}>
              <Typography mb={2}>Input Fields</Typography>
              {Object.entries(modifiedInput?.properties).map(
                ([key, value]: any, index) => (
                  <Accordion
                    key={index}
                    expanded={expandedInput === `panel${index}`}
                    onChange={handleChangeInput(`panel${index}`)}
                    sx={{ margin: "0px !important" }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${index}-content`}
                      id={`panel${index}-header`}
                    >
                      <Typography
                        component="span"
                        sx={{
                          width: "33%",
                          flexShrink: 0,
                          textTransform: "capitalize",
                        }}
                      >
                        {capitalizeFirstLetter(key)}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ color: "text.secondary" }}
                      >
                        {value.input}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails
                      sx={{ borderTop: "1px solid rgba(0,0,0,0.12)" }}
                    >
                      <Stack flexDirection={"column"}>
                        <Stack flexDirection={"row"} justifyContent={"end"}>
                          <FormControlLabel
                            disabled
                            control={<Checkbox color="secondary" />}
                            label="UNIQ"
                          />
                          <FormControlLabel
                            disabled
                            control={<Checkbox color="secondary" />}
                            label="Not Mutable"
                          />
                        </Stack>
                        <TextField
                          multiline
                          variant="standard"
                          id="outlined-required"
                          label="Description"
                          value={
                            value.description !== null ? value.description : ""
                          }
                          rows="auto"
                          InputProps={{
                            readOnly: true,
                          }}
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          color="secondary"
                          sx={{
                            "& .MuiInput-underline:before": {
                              borderBottomStyle: "none !important",
                            },
                            marginBottom: "8px",
                          }}
                        />
                        <Grid container spacing={2}>
                          {textFields.map((field, index) => (
                            <Grid key={index} size={3}>
                              <>
                                <TextField
                                  variant="standard"
                                  id={`outlined-${field.label
                                    .toLowerCase()
                                    .replace(" ", "-")}`}
                                  label={capitalizeFirstLetter(field.label)}
                                  value={value[`${field.label}`] || "null"}
                                  slotProps={{
                                    inputLabel: {
                                      shrink: true,
                                    },
                                  }}
                                  color="secondary"
                                  sx={{
                                    "& .MuiInput-underline:before": {
                                      borderBottomStyle: "none !important",
                                    },
                                  }}
                                />
                              </>
                            </Grid>
                          ))}
                        </Grid>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )
              )}
            </Grid>
            <Grid size={6}>
              <Typography mb={2}>Output Fields</Typography>
              {Object.entries(modifiedOutput?.properties).map(
                ([key, value]: any, index) => (
                  <Accordion
                    key={index}
                    expanded={expandedOutput === `panel${index}`}
                    onChange={handleChangeOutput(`panel${index}`)}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${index}-content`}
                      id={`panel${index}-header`}
                    >
                      <Typography
                        component="span"
                        sx={{ width: "33%", flexShrink: 0 }}
                      >
                        {capitalizeFirstLetter(key)}
                      </Typography>
                      <Typography
                        component="span"
                        sx={{ color: "text.secondary", marginLeft: "100px" }}
                      >
                        {value.input}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack flexDirection={"column"}>
                        <Stack flexDirection={"row"} justifyContent={"end"}>
                          <FormControlLabel
                            disabled
                            control={<Checkbox color="secondary" />}
                            label="UNIQ"
                          />
                          <FormControlLabel
                            disabled
                            control={<Checkbox color="secondary" />}
                            label="Not Mutable"
                          />
                        </Stack>
                        <TextField
                          multiline
                          variant="standard"
                          id="outlined-required"
                          label="Description"
                          value={
                            value.description !== null ? value.description : ""
                          }
                          InputProps={{
                            readOnly: true,
                          }}
                          slotProps={{
                            inputLabel: {
                              shrink: true,
                            },
                          }}
                          color="secondary"
                          sx={{
                            "& .MuiInput-underline:before": {
                              borderBottomStyle: "none !important",
                            },
                            marginBottom: "8px",
                          }}
                          rows="auto"
                        />
                        <Grid container spacing={2}>
                          {textFields.map((field, index) => (
                            <Grid key={index} size={3}>
                              <TextField
                                variant="standard"
                                id={`outlined-${field.label
                                  .toLowerCase()
                                  .replace(" ", "-")}`}
                                label={capitalizeFirstLetter(field.label)}
                                value={value[`${field.label}`] || "null"}
                                slotProps={{
                                  inputLabel: {
                                    shrink: true,
                                  },
                                }}
                                color="secondary"
                                sx={{
                                  "& .MuiInput-underline:before": {
                                    borderBottomStyle: "none !important",
                                  },
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )
              )}
            </Grid>
          </Grid>
        </Stack>
      </Paper>
    </>
  );
};

export default QueryName;
